/**
 * The per-user hidden-meeting set (apps/meetings/api/hidden_meeting_api.py),
 * cached client-side under a single, stable query key.
 *
 * There is no standalone GET endpoint for this data — the cache is seeded by:
 *   1. The dashboard prefetch (lib/prefetch/entries/dashboard.ts), from the
 *      combined quickfetch response's `hidden_meetings` field.
 *   2. The dismiss/undismiss mutation (useDismissMeeting.ts), which updates it
 *      optimistically and reconciles on the server response.
 *   3. The useCalendarEvents fallback seed (pages/dashboard/hooks/useDashboardData.ts),
 *      which calls `seedEmptyHiddenMeetingsIfUnseeded` below if the cache is
 *      still unseeded a short time after mount. This covers sessions that
 *      reach the dashboard without lib/prefetch.ts ever executing — e.g. a
 *      client-side navigation from `/` via HomeRedirect/OrgGuard/
 *      FullPageOrgSwitcher, none of which trigger a hard page load.
 *
 * useHiddenMeetings is therefore a pure cache reader — `enabled: false` means
 * its queryFn never runs; `data` only ever comes from a `setQueryData` call at
 * one of the three seed sites above.
 */
import {
  type QueryClient,
  type QueryKey,
  useQuery,
} from '@tanstack/react-query';
import { useMemo } from 'react';

export interface HiddenMeetings {
  seriesIds: string[];
  meetingEntityIds: string[];
}

export const EMPTY_HIDDEN_MEETINGS: HiddenMeetings = {
  seriesIds: [],
  meetingEntityIds: [],
};

export function hiddenMeetingsQueryKey(orgId: number): QueryKey {
  return ['dashboard', 'hiddenMeetings', orgId] as const;
}

export interface UseHiddenMeetingsResult {
  hiddenMeetings: HiddenMeetings;
  /** True once the cache has been seeded at least once (prefetch, a dismiss/undismiss call, or the fallback seed). */
  isLoaded: boolean;
}

export function useHiddenMeetings(
  orgId: number | undefined
): UseHiddenMeetingsResult {
  const { data, isSuccess } = useQuery({
    queryKey: hiddenMeetingsQueryKey(orgId ?? 0),
    queryFn: () => Promise.resolve(EMPTY_HIDDEN_MEETINGS),
    enabled: false,
    staleTime: Infinity,
  });

  return useMemo(
    () => ({
      hiddenMeetings: data ?? EMPTY_HIDDEN_MEETINGS,
      isLoaded: isSuccess,
    }),
    [data, isSuccess]
  );
}

/**
 * Fail-open seed: writes EMPTY_HIDDEN_MEETINGS into the cache only if
 * nothing has seeded it yet, unblocking `useHiddenMeetings`' `isLoaded` gate
 * without waiting on a real prefetch or dismiss/undismiss result.
 *
 * Cache-only by design — callers must NEVER persist this value to
 * localStorage (see PersistedDashboardPrefetch), since it doesn't reflect a
 * server-confirmed hidden set. The guard only protects against clobbering an
 * *already-seeded* cache entry; it does not protect this fallback value
 * itself — a genuine prefetch result that lands afterward always overwrites
 * it via an unconditional `setQueryData` (see the dashboard prefetch entry's
 * `hiddenLoaded` branch), which is the desired behavior.
 *
 * Shared by two call sites:
 *   - The dashboard prefetch entry's failure path (whole-quickfetch failure,
 *     or `hidden_meetings: null` in the response).
 *   - The useCalendarEvents fallback effect, which fires when the prefetch
 *     script never ran for this session at all (see the module doc above).
 */
export function seedEmptyHiddenMeetingsIfUnseeded(
  queryClient: QueryClient,
  orgId: number
): void {
  if (queryClient.getQueryData(hiddenMeetingsQueryKey(orgId)) === undefined) {
    queryClient.setQueryData<HiddenMeetings>(
      hiddenMeetingsQueryKey(orgId),
      EMPTY_HIDDEN_MEETINGS
    );
  }
}
